import Keycloak, { type KeycloakInitOptions } from "keycloak-js";

/**
 * Keycloak client singleton.
 *
 * Config points at the shared ClinicOS Keycloak instance
 * (realm `clinicos`, client `pulseboard-web`) per prebuild-docs/14 and the
 * live realm provisioned in scripts/setup-keycloak-client.sh.
 *
 * Why a module-level singleton (not per-render): keycloak-js mutates state
 * on the instance (`keycloak.token`, `keycloak.authenticated`) and must not
 * be re-initialised. StrictMode double-effect in dev would otherwise invoke
 * `init()` twice — which keycloak-js rejects.
 */
const url = import.meta.env.VITE_KEYCLOAK_URL;
const realm = import.meta.env.VITE_KEYCLOAK_REALM;
const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

/**
 * Behaviour when env vars are missing:
 *  - Dev (`import.meta.env.DEV`): warn + use safe-ish fallbacks so the public
 *    pages still render. init() will reject, AuthContext sets `initError`,
 *    and authed pages surface a friendly banner. Unblocks running `bun run dev`
 *    without a local `.env`.
 *  - Prod (`import.meta.env.PROD`): throw loudly — misconfigured prod should
 *    fail at boot, not ship a broken auth surface.
 */
if (!url || !realm || !clientId) {
  const msg =
    "Missing VITE_KEYCLOAK_* env vars. See apps/web/.env.example.";
  if (import.meta.env.PROD) {
    throw new Error(msg);
  }
  console.warn(`[auth] ${msg} Falling back for dev — auth will not work.`);
}

export const keycloak = new Keycloak({
  url: url ?? "https://auth.clinicos.com.au",
  realm: realm ?? "clinicos",
  clientId: clientId ?? "pulseboard-web",
});

/**
 * PKCE S256 is non-negotiable — this is the setting that has bitten every
 * prior ClinicOS module per prebuild-docs/14 §2. Do not change to "plain".
 *
 * `check-sso` mode: attempts a silent iframe-based SSO check on boot.
 *  - Anonymous visitors to the public directory stay anonymous (no redirect).
 *  - Users with an existing realm session get seamless auth (no flash of
 *    unauthenticated content, no login click needed).
 *  - If the iframe is blocked (Safari ITP, incognito), falls back to
 *    unauthenticated — public browsing still works, login prompts on demand.
 */
const canUseSilentSso =
  typeof window !== "undefined" &&
  (window.location.hostname === "pulseboard.com.au" ||
    window.location.hostname === "www.pulseboard.com.au" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");
const browserOrigin =
  typeof window !== "undefined" ? window.location.origin : "http://localhost";

export const KEYCLOAK_INIT_OPTIONS: KeycloakInitOptions = {
  ...(canUseSilentSso ? { onLoad: "check-sso" as const } : {}),
  silentCheckSsoRedirectUri: `${browserOrigin}/silent-check-sso.html`,
  // iOS/Safari often blocks the hidden SSO iframe. Keep public PulseBoard
  // pages public instead of falling back to a full-page auth redirect.
  silentCheckSsoFallback: false,
  pkceMethod: "S256",
  checkLoginIframe: false,
  enableLogging: import.meta.env.DEV,
};

export function loginRedirectUri(
  path = `${window.location.pathname}${window.location.search}`,
): string {
  // Always return to the current origin. Redirecting via the apex
  // (pulseboard.com.au) bounced through a 302 to the city home page and
  // dropped the OAuth code, so nobody could sign in from a deep link.
  return `${window.location.origin}${path}`;
}

/**
 * Helper: ensure the token is valid for at least `minValiditySeconds` more
 * seconds. Used by the API client before every authenticated request so we
 * never send a stale token.
 */
export async function ensureFreshToken(
  minValiditySeconds = 60,
): Promise<string | undefined> {
  if (!keycloak.authenticated) return undefined;
  try {
    await keycloak.updateToken(minValiditySeconds);
    return keycloak.token;
  } catch {
    // Refresh failed (refresh token expired / revoked). Force re-auth.
    await keycloak.login();
    return undefined;
  }
}
